// useShortcuts composable — 키보드 단축키 등록 및 cleanup (ux-patterns.md §12.2)
import { ref, onMounted, onBeforeUnmount } from 'vue';

// @MX:SPEC: SPEC-ECOUNT-ETL-OPS-CONSOLE-001 REQ-OC-COMMON-006

/**
 * input/textarea 포커스 격리 판별 (ux-patterns.md §3.2)
 * @returns {boolean}
 */
function isInputFocused() {
  const el = document.activeElement;
  return (
    el &&
    (el.tagName === 'INPUT' ||
      el.tagName === 'TEXTAREA' ||
      el.tagName === 'SELECT' ||
      el.isContentEditable ||
      el.classList.contains('ant-input') ||
      el.classList.contains('ant-select-selection-search-input'))
  );
}

/**
 * 키보드 단축키 composable
 * @param {Object} shortcuts - 단축키 맵
 *   키: 단일 키('r', '?') 또는 시퀀스('g+d', 'g+r')
 *   값: handler 함수 또는 { handler, disabled: () => boolean }
 * @returns {{ isAwaitingSequence: Ref<boolean> }}
 */
export function useShortcuts(shortcuts) {
  const isAwaitingSequence = ref(false);
  let awaitingTimeout = null;

  function handleKeydown(event) {
    // input 포커스 격리
    if (isInputFocused()) return;

    const key = event.key;

    // g 시퀀스 대기 중
    if (isAwaitingSequence.value) {
      clearTimeout(awaitingTimeout);
      isAwaitingSequence.value = false;
      awaitingTimeout = null;

      const seqKey = `g+${key}`;
      const handler = shortcuts[seqKey];
      if (handler) {
        event.preventDefault();
        const fn = typeof handler === 'function' ? handler : handler.handler;
        const disabled = handler.disabled;
        if (disabled && disabled()) return;
        fn();
      }
      return;
    }

    // g 키 — 시퀀스 시작
    if (key === 'g') {
      // g+* 조합 단축키가 있으면 대기 모드
      const hasSeqShortcuts = Object.keys(shortcuts).some((k) => k.startsWith('g+'));
      if (hasSeqShortcuts) {
        event.preventDefault();
        isAwaitingSequence.value = true;
        awaitingTimeout = setTimeout(() => {
          isAwaitingSequence.value = false;
          awaitingTimeout = null;
        }, 1000);
        return;
      }
    }

    // 일반 단일 키
    const handler = shortcuts[key];
    if (handler) {
      event.preventDefault();
      const fn = typeof handler === 'function' ? handler : handler.handler;
      const disabled = handler.disabled;
      if (disabled && disabled()) return;
      fn();
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
    if (awaitingTimeout) {
      clearTimeout(awaitingTimeout);
    }
  });

  return { isAwaitingSequence };
}
